import { useCallback, useEffect, useState } from "react";
import { ApiRequestError, type AppointmentPhoto, type ContactProperty, type PropertyRecord } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import type { AppointmentLogJoin } from "@/repositories/appointment-history";
import type { AppointmentJoin } from "@/repositories/appointments.repository";
import { PropertyContactRepository, type ContactAppointmentQuery } from "@/repositories/property-contact.repository";

type Read<T> = { value: T | null; loading: boolean; notFound: boolean; error: string | null };

const isNotFound = (error: unknown) => error instanceof ApiRequestError && error.status === 404;

const messageOf = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

function useRead<T>(read: () => Promise<T>, failure: string): Read<T> {
  const [state, setState] = useState<Read<T>>({ value: null, loading: true, notFound: false, error: null });

  useEffect(() => {
    let current = true;
    setState((previous) => ({ ...previous, loading: true, notFound: false, error: null }));
    read().then(
      (value) => current && setState({ value, loading: false, notFound: false, error: null }),
      (error: unknown) => current && setState({ value: null, loading: false, notFound: isNotFound(error), error: isNotFound(error) ? null : messageOf(error, failure) }),
    );
    return () => {
      current = false;
    };
  }, [read, failure]);

  return state;
}

export function useMyContactProperties(): Read<ContactProperty[]> {
  return useRead(PropertyContactRepository.mine, "Failed to load the properties you are a contact for");
}

export function useContactProperty(id: uuid): Read<PropertyRecord> {
  const read = useCallback(() => PropertyContactRepository.property(id), [id]);
  return useRead(read, "Failed to load the property");
}

export function useContactAppointments({ property, status, search, page, pageSize, sortKey, sortOrder }: ContactAppointmentQuery): Read<{ data: AppointmentJoin[]; count: number | null }> {
  const read = useCallback(
    () => PropertyContactRepository.appointments({ property, status, search, page, pageSize, sortKey, sortOrder }),
    [property, status, search, page, pageSize, sortKey, sortOrder],
  );
  return useRead(read, "Failed to load the appointments");
}

export type ContactAppointmentRecord = { appointment: AppointmentJoin; history: AppointmentLogJoin[]; photos: AppointmentPhoto[] };

export function useContactAppointment(id: uuid): Read<ContactAppointmentRecord> {
  const read = useCallback(async () => {
    const [appointment, history, photos] = await Promise.all([PropertyContactRepository.appointment(id), PropertyContactRepository.history(id), PropertyContactRepository.photos(id)]);
    return { appointment, history, photos };
  }, [id]);
  return useRead(read, "Failed to load the appointment");
}
